import Linkedin from "@/components/svg/Linkedin";
import LinkIcon from "@/components/svg/LinkIcon";
import { asText, isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const richTextBase = {
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

/**
 * @param {{ member: import("@prismicio/client").Content.KrewSliceDefaultPrimaryMembersItem }} props
 */
const MemberCard = ({ member }) => {
  const { picture, name, role, socials, text } = member;
  // Legacy content stored the name as a key text.
  const memberName = Array.isArray(name) ? asText(name) : name;
  const filledSocials = Array.isArray(socials)
    ? socials.filter((link) => isFilled.link(link))
    : [];

  return (
    <li className="flex flex-col gap-7 rounded-[10px] bg-beige-50 px-5 py-[30px]">
      <div className="flex items-center gap-7">
        {isFilled.image(picture) && (
          <PrismicNextImage
            field={picture}
            fallbackAlt=""
            className="size-[100px] shrink-0 rounded-full object-cover"
            sizes="100px"
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            {isFilled.keyText(memberName) && (
              <h3 className="p-lg-n sbd text-bleu">{memberName}</h3>
            )}
            {isFilled.keyText(role) && (
              <p className="p-sm-n text-bleu">{role}</p>
            )}
          </div>

          {filledSocials.length > 0 && (
            <div className="flex gap-[5px]">
              {filledSocials.map((link, index) => {
                const isLinkedin = link.variant === "LinkedIn";
                const label = isLinkedin ? "LinkedIn" : "Site web";

                return (
                  <PrismicNextLink
                    key={link.key ?? index}
                    prefetch={false}
                    field={link}
                    aria-label={memberName ? `${label} – ${memberName}` : label}
                    className="rounded-[5px]"
                  >
                    {isLinkedin ? <Linkedin /> : <LinkIcon />}
                  </PrismicNextLink>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isFilled.richText(text) && (
        <PrismicRichText
          field={text}
          components={{
            ...richTextBase,
            paragraph: ({ children }) => (
              <p className="p-base-n text-bleu not-last:mb-5">{children}</p>
            ),
          }}
        />
      )}
    </li>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.KrewSlice} KrewSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<KrewSlice>} KrewProps
 * @type {import("react").FC<KrewProps>}
 */
const Krew = ({ slice }) => {
  const { sub_header, title, text, members } = slice.primary;

  const filledMembers = Array.isArray(members)
    ? members.filter(
        (member) =>
          isFilled.image(member.picture) ||
          isFilled.richText(member.name) ||
          isFilled.keyText(member.name) ||
          isFilled.keyText(member.role) ||
          isFilled.richText(member.text),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-lg flex flex-col gap-10 lg:gap-15">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
          <div className="flex flex-col gap-2.5 lg:max-w-[575px]">
            <div className="flex flex-col gap-1.5">
              {isFilled.keyText(sub_header) && (
                <p className="section-text-n text-rouge">{sub_header}</p>
              )}
              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="h2-n lg:dk text-bleu">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="h2-n lg:dk text-bleu">{children}</h3>
                    ),
                  }}
                />
              )}
            </div>
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  ...richTextBase,
                  paragraph: ({ children }) => (
                    <p className="p-lg-n text-bleu not-last:mb-5">{children}</p>
                  ),
                }}
              />
            )}
          </div>
        )}

        {filledMembers.length > 0 && (
          <ul className="grid grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3 lg:gap-x-10">
            {filledMembers.map((member, index) => (
              <MemberCard key={`member-${index}`} member={member} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default Krew;
